import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  ConfirmModal,
  Skeleton,
  StatusBadge,
  StatusTimeline,
  SectionView,
  Tabs,
  useToast,
  REPORT_PART_LABELS,
  REPORT_PART_ORDER,
} from '@rb/ui'
import type { ProjectStatus, Section, ReportPart } from '@rb/ui'
import { getCurrentUser } from '../lib/auth'
import { useFetch } from '../hooks/useFetch'
import { apiFetch } from '../utils/api'
import styles from './AdminProjectDetail.module.css'

// ─── Types ──────────────────────────────────────────────────────────────────

interface AdminFile {
  id: string
  filename: string
  file_type: string | null
  quality_report: { status: 'ok' | 'warning' | 'error'; issues: string[] } | null
  extracted_data: unknown | null
  issue_code: string | null
  issue_title: string | null
  issue_description: string | null
  created_at: string
}

interface AdminProject {
  id: string
  name: string
  status: string
  created_at: string
  updated_at: string
  internal_notes: string | null
  client_email: string
  client_name: string | null
}

interface AnalysisResult {
  id: string
  version: number
  analysis: unknown | null
  proposal: unknown | null
  roi: unknown | null
  financing: unknown | null
  created_at: string
  published_at: string | null
}

interface AdminDetailResponse {
  project: AdminProject
  files: AdminFile[]
  analysis_results: AnalysisResult | null
}

interface ActivityEntry {
  id: string
  actor_type: string
  action: string
  details: Record<string, unknown> | null
  created_at: string
  user_email: string | null
  user_name: string | null
}

interface ActivityResponse {
  activity: ActivityEntry[]
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const ISSUE_CODES = [
  { value: 'not_expected_content', label: 'Datei enthält nicht den erwarteten Inhalt' },
  { value: 'data_mismatch', label: 'Datenmismatch — Daten stimmen nicht überein' },
  { value: 'unreadable', label: 'Datei nicht lesbar' },
  { value: 'other', label: 'Sonstige' },
]

const STATUS_OPTIONS = [
  { value: 'uploaded', label: 'Hochgeladen' },
  { value: 'in_review', label: 'In Prüfung' },
  { value: 'analysis_in_progress', label: 'Analyse läuft' },
  { value: 'action_needed', label: 'Aktion erforderlich' },
  { value: 'results_available', label: 'Ergebnis verfügbar' },
]

const ACTION_LABELS: Record<string, string> = {
  status_changed: 'Status geändert',
  file_issue_flagged: 'Datei-Issue gemeldet',
  file_issue_cleared: 'Datei-Issue gelöscht',
  analysis_uploaded: 'Analyseergebnisse hochgeladen',
  analysis_published: 'Ergebnisse veröffentlicht',
}

function formatDate(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatDateTime(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ─── Component ──────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'preview', label: 'Ergebnis' },
  { id: 'activity', label: 'Aktivitätslog' },
]

const PART_COLUMN: Record<ReportPart, 'analysis' | 'proposal' | 'roi' | 'financing'> = {
  analyse: 'analysis',
  vorschlag: 'proposal',
  roi: 'roi',
  finanzierung: 'financing',
}

export default function AdminProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const { data, loading, error, reload } = useFetch<AdminDetailResponse>(
    `/admin/projects/${id}`
  )
  const {
    data: activityData,
    loading: activityLoading,
    reload: reloadActivity,
  } = useFetch<ActivityResponse>(`/admin/projects/${id}/activity`)

  const [activeTab, setActiveTab] = useState('overview')

  // Status transition
  const [newStatus, setNewStatus] = useState('')
  const [changingStatus, setChangingStatus] = useState(false)

  // Issue form
  const [issuingFileId, setIssuingFileId] = useState<string | null>(null)
  const [issueCode, setIssueCode] = useState('not_expected_content')
  const [issueTitle, setIssueTitle] = useState('')
  const [issueDescription, setIssueDescription] = useState('')
  const [issueSaving, setIssueSaving] = useState(false)

  // Clear issue
  const [clearingIssueId, setClearingIssueId] = useState<string | null>(null)

  // Analysis upload
  const analysisFileRef = useRef<HTMLInputElement>(null)
  const [analysisJson, setAnalysisJson] = useState('')
  const [analysisJsonError, setAnalysisJsonError] = useState('')
  const [uploadingAnalysis, setUploadingAnalysis] = useState(false)

  // Publish
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)

  // Internal notes
  const [notes, setNotes] = useState('')
  const notesInitRef = useRef(false)
  const [savingNotes, setSavingNotes] = useState(false)

  // Extracted data viewer
  const [viewingExtracted, setViewingExtracted] = useState<{
    filename: string
    data: unknown
  } | null>(null)

  useEffect(() => {
    if (data && !notesInitRef.current) {
      setNotes(data.project.internal_notes ?? '')
      notesInitRef.current = true
    }
  }, [data])

  useEffect(() => {
    notesInitRef.current = false
  }, [id])

  // Preview tab scroll-spy: highlight the nav item for the section near the top.
  const previewContentRef = useRef<HTMLDivElement>(null)
  const [activePreviewId, setActivePreviewId] = useState('')

  useEffect(() => {
    if (activeTab !== 'preview') return
    const root = previewContentRef.current
    if (!root) return
    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-section-id]'))
    if (!els.length) return
    setActivePreviewId((cur) => cur || els[0].dataset.sectionId || '')

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        const top = visible[0]?.target as HTMLElement | undefined
        if (top?.dataset.sectionId) setActivePreviewId(top.dataset.sectionId)
      },
      { rootMargin: '-72px 0px -55% 0px', threshold: 0 }
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [activeTab, data])

  function scrollToPreviewSection(sectionId: string) {
    const el = previewContentRef.current?.querySelector<HTMLElement>(
      `[data-section-id="${sectionId}"]`
    )
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActivePreviewId(sectionId)
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async function handleStatusChange() {
    if (!newStatus || !id) return
    setChangingStatus(true)
    try {
      await apiFetch(`/admin/projects/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: newStatus }),
      })
      showToast('Status geändert', 'success')
      setNewStatus('')
      reload()
      reloadActivity()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler', 'error')
    } finally {
      setChangingStatus(false)
    }
  }

  async function handleFlagIssue(fileId: string) {
    if (!id) return
    setIssueSaving(true)
    try {
      await apiFetch(`/admin/projects/${id}/files/${fileId}/issue`, {
        method: 'POST',
        body: JSON.stringify({
          issue_code: issueCode,
          issue_title: issueCode === 'other' ? issueTitle : undefined,
          issue_description: issueDescription,
        }),
      })
      showToast('Issue gemeldet — Kunde wird benachrichtigt', 'success')
      setIssuingFileId(null)
      reload()
      reloadActivity()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler', 'error')
    } finally {
      setIssueSaving(false)
    }
  }

  async function handleClearIssue(fileId: string) {
    if (!id) return
    setClearingIssueId(fileId)
    try {
      await apiFetch(`/admin/projects/${id}/files/${fileId}/clear-issue`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      showToast('Issue gelöscht', 'success')
      reload()
      reloadActivity()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler', 'error')
    } finally {
      setClearingIssueId(null)
    }
  }

  async function handleDownload(fileId: string, filename: string) {
    const user = getCurrentUser()
    try {
      const res = await fetch(`/api/admin/projects/${id}/files/${fileId}/download`, {
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
      })
      if (!res.ok) {
        showToast('Download fehlgeschlagen', 'error')
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      showToast('Download fehlgeschlagen', 'error')
    }
  }

  function handleAnalysisFileLoad(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setAnalysisJson((ev.target?.result as string) ?? '')
      setAnalysisJsonError('')
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  async function handleUploadAnalysis() {
    if (!id || !analysisJson.trim()) return
    setAnalysisJsonError('')
    let parsed: unknown
    try {
      parsed = JSON.parse(analysisJson)
    } catch {
      setAnalysisJsonError('Ungültiges JSON')
      return
    }
    setUploadingAnalysis(true)
    try {
      await apiFetch(`/admin/projects/${id}/analysis`, {
        method: 'POST',
        body: JSON.stringify(parsed),
      })
      showToast('Analyseergebnisse hochgeladen', 'success')
      setAnalysisJson('')
      reload()
      reloadActivity()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler', 'error')
    } finally {
      setUploadingAnalysis(false)
    }
  }

  async function handlePublish() {
    if (!id) return
    setPublishing(true)
    try {
      await apiFetch(`/admin/projects/${id}/analysis/publish`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      showToast('Ergebnisse veröffentlicht — Kunde wird benachrichtigt', 'success')
      setPublishConfirmOpen(false)
      reload()
      reloadActivity()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler', 'error')
    } finally {
      setPublishing(false)
    }
  }

  async function handleSaveNotes() {
    if (!id) return
    setSavingNotes(true)
    try {
      await apiFetch(`/admin/projects/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      })
      showToast('Notizen gespeichert', 'success')
    } catch {
      // silent — next blur will retry
    } finally {
      setSavingNotes(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton className={styles.skeletonTitle} />
        <Skeleton className={styles.skeletonRow} />
        <Skeleton className={styles.skeletonRow} />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <p>Projekt nicht gefunden.</p>
          <Button variant="secondary" onClick={() => navigate('/admin/projects')}>
            Zurück
          </Button>
        </div>
      </div>
    )
  }

  const { project, files, analysis_results } = data
  const status = project.status as ProjectStatus

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate('/admin/projects')}>
        ← Alle Projekte
      </button>

      <div className={styles.header}>
        <div className={styles.clientLine}>
          <span>{project.client_email}</span>
          {project.client_name && (
            <>
              <span className={styles.clientSep}>·</span>
              <span>{project.client_name}</span>
            </>
          )}
        </div>
        <div className={styles.headerMain}>
          <h1 className={styles.heading}>{project.name || '(Ohne Titel)'}</h1>
          <StatusBadge status={status} />
        </div>
      </div>

      <Tabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ── Übersicht tab ── */}
      {activeTab === 'overview' && (
        <>
          <div className={styles.timelineWrap}>
            <StatusTimeline status={status} />
          </div>

          {/* Status transition */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Status ändern</h2>
            <div className={styles.statusControl}>
              <select
                className={styles.select}
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">Status wählen…</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Button
                variant="secondary"
                onClick={handleStatusChange}
                disabled={!newStatus || changingStatus}
              >
                {changingStatus ? 'Wird gespeichert…' : 'Status setzen'}
              </Button>
            </div>
          </section>

          {/* Files */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Dateien
              <span className={styles.fileCount}>{files.length}</span>
            </h2>
            <div className={styles.fileList}>
              {files.map((file) => (
                <div key={file.id} className={styles.adminFileCard}>
                  {/* File row */}
                  <div className={styles.adminFileRow}>
                    <div className={styles.adminFileInfo}>
                      <span className={styles.adminFileName} title={file.filename}>
                        {file.filename}
                      </span>
                      <span className={styles.adminFileDate}>{formatDate(file.created_at)}</span>
                    </div>
                    <div className={styles.adminFileActions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleDownload(file.id, file.filename)}
                      >
                        Herunterladen
                      </button>
                      {file.extracted_data && (
                        <button
                          className={styles.actionBtn}
                          onClick={() =>
                            setViewingExtracted({ filename: file.filename, data: file.extracted_data })
                          }
                        >
                          Extrahierte Daten
                        </button>
                      )}
                      {file.issue_code ? (
                        <button
                          className={styles.actionBtnDestructive}
                          onClick={() => handleClearIssue(file.id)}
                          disabled={clearingIssueId === file.id}
                        >
                          {clearingIssueId === file.id ? '…' : 'Issue löschen'}
                        </button>
                      ) : (
                        <button
                          className={styles.actionBtnWarning}
                          onClick={() => {
                            setIssuingFileId(issuingFileId === file.id ? null : file.id)
                            setIssueCode('not_expected_content')
                            setIssueTitle('')
                            setIssueDescription('')
                          }}
                        >
                          Issue melden
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Existing issue */}
                  {file.issue_code && (
                    <div className={styles.existingIssue}>
                      <p className={styles.existingIssueTitle}>{file.issue_title}</p>
                      <p className={styles.existingIssueDesc}>{file.issue_description}</p>
                    </div>
                  )}

                  {/* Issue form (inline) */}
                  {issuingFileId === file.id && (
                    <div className={styles.issuePanel}>
                      <p className={styles.issuePanelTitle}>Issue melden</p>

                      <div className={styles.formRow}>
                        <label className={styles.label}>Issue-Typ</label>
                        <select
                          className={styles.select}
                          value={issueCode}
                          onChange={(e) => setIssueCode(e.target.value)}
                        >
                          {ISSUE_CODES.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {issueCode === 'other' && (
                        <div className={styles.formRow}>
                          <label className={styles.label}>Titel (max. 50 Zeichen)</label>
                          <input
                            className={styles.input}
                            placeholder="Kurzer Titel für den Kunden…"
                            maxLength={50}
                            value={issueTitle}
                            onChange={(e) => setIssueTitle(e.target.value)}
                          />
                          <div className={styles.charCount}>{issueTitle.length}/50</div>
                        </div>
                      )}

                      <div className={styles.formRow}>
                        <label className={styles.label}>Beschreibung für den Kunden</label>
                        <textarea
                          className={styles.textarea}
                          placeholder="Was genau ist das Problem? Wie soll der Kunde reagieren?"
                          rows={3}
                          value={issueDescription}
                          onChange={(e) => setIssueDescription(e.target.value)}
                        />
                      </div>

                      <div className={styles.issuePanelActions}>
                        <Button
                          variant="ghost"
                          onClick={() => setIssuingFileId(null)}
                          disabled={issueSaving}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => handleFlagIssue(file.id)}
                          disabled={
                            issueSaving ||
                            !issueDescription.trim() ||
                            (issueCode === 'other' && !issueTitle.trim())
                          }
                        >
                          {issueSaving ? '…' : 'Issue melden'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {files.length === 0 && (
                <div className={styles.emptyActivity}>Noch keine Dateien hochgeladen.</div>
              )}
            </div>
          </section>

          {/* Analysis */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Analyseergebnisse</h2>

            {analysis_results && (
              <div className={styles.analysisInfo}>
                <div className={styles.analysisMeta}>
                  Version {analysis_results.version} · hochgeladen{' '}
                  {formatDate(analysis_results.created_at)}
                </div>
                {analysis_results.published_at ? (
                  <span className={styles.publishedBadge}>
                    Veröffentlicht {formatDate(analysis_results.published_at)}
                  </span>
                ) : (
                  <Button variant="primary" onClick={() => setPublishConfirmOpen(true)}>
                    Ergebnisse veröffentlichen
                  </Button>
                )}
              </div>
            )}

            <p className={styles.jsonHint}>
              {analysis_results
                ? 'Neue Version hochladen (überschreibt vorherige):'
                : 'JSON-Payload einfügen oder Datei laden:'}
            </p>

            <div className={styles.statusControl}>
              <Button
                variant="ghost"
                onClick={() => analysisFileRef.current?.click()}
              >
                JSON-Datei laden
              </Button>
              <input
                ref={analysisFileRef}
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={handleAnalysisFileLoad}
              />
            </div>

            <textarea
              className={styles.jsonTextarea}
              placeholder='{"analysis": {...}, "proposal": {...}, "roi": {...}, "financing": {...}}'
              rows={8}
              value={analysisJson}
              onChange={(e) => {
                setAnalysisJson(e.target.value)
                setAnalysisJsonError('')
              }}
            />
            {analysisJsonError && (
              <div className={styles.errorText}>{analysisJsonError}</div>
            )}
            <div>
              <Button
                variant="secondary"
                onClick={handleUploadAnalysis}
                disabled={!analysisJson.trim() || uploadingAnalysis}
              >
                {uploadingAnalysis ? 'Wird hochgeladen…' : 'Analyse hochladen'}
              </Button>
            </div>
          </section>

          {/* Internal notes */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Interne Notizen</h2>
            <p className={styles.notesHint}>Nur für Admins sichtbar. Automatisches Speichern beim Verlassen.</p>
            <textarea
              className={styles.textarea}
              rows={5}
              placeholder="Interne Notizen zum Projekt…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={handleSaveNotes}
            />
            {savingNotes && <p className={styles.notesHint}>Wird gespeichert…</p>}
          </section>
        </>
      )}

      {/* ── Ergebnis tab ── */}
      {activeTab === 'preview' && (
        !analysis_results ? (
          <div className={styles.emptyActivity}>
            Noch keine Analyseergebnisse hochgeladen.
          </div>
        ) : (
          (() => {
            const previewParts = REPORT_PART_ORDER.map((part) => {
              const col = PART_COLUMN[part]
              const sections = Array.isArray(analysis_results[col])
                ? (analysis_results[col] as Section[])
                : []
              return { part, sections }
            })
            const hasSections = previewParts.some((p) => p.sections.length > 0)
            if (!hasSections) {
              return (
                <div className={styles.previewParts}>
                  {previewParts.map(({ part }) => (
                    <div key={part} className={styles.previewPart}>
                      <h2 className={styles.previewPartHeading}>{REPORT_PART_LABELS[part]}</h2>
                      <p className={styles.previewPartEmpty}>noch nicht verfügbar</p>
                    </div>
                  ))}
                </div>
              )
            }
            return (
              <div className={styles.previewLayout}>
                <nav className={styles.previewNav} aria-label="Abschnitte">
                  {previewParts.map(({ part, sections }) =>
                    sections.length === 0 ? null : (
                      <div key={part} className={styles.previewNavGroup}>
                        <div className={styles.previewNavGroupLabel}>
                          {REPORT_PART_LABELS[part]}
                        </div>
                        <ul className={styles.previewNavList}>
                          {sections.map((section, si) => {
                            const sid = `prev-${part}-${si}`
                            return (
                              <li key={sid}>
                                <button
                                  className={[
                                    styles.previewNavItem,
                                    activePreviewId === sid ? styles.previewNavItemActive : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  aria-current={activePreviewId === sid ? 'true' : undefined}
                                  onClick={() => scrollToPreviewSection(sid)}
                                >
                                  {section.section_title}
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )
                  )}
                </nav>

                <div className={styles.previewContent} ref={previewContentRef}>
                  {previewParts.map(({ part, sections }) => (
                    <div key={part} className={styles.previewPart}>
                      <h2 className={styles.previewPartHeading}>{REPORT_PART_LABELS[part]}</h2>
                      {sections.length === 0 ? (
                        <p className={styles.previewPartEmpty}>noch nicht verfügbar</p>
                      ) : (
                        sections.map((section, si) => {
                          const sid = `prev-${part}-${si}`
                          return (
                            <div
                              key={si}
                              data-section-id={sid}
                              className={styles.previewSectionWrap}
                            >
                              <h3 className={styles.previewSectionTitle}>
                                {section.section_title}
                              </h3>
                              <SectionView section={section} />
                            </div>
                          )
                        })
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })()
        )
      )}

      {/* ── Aktivitätslog tab ── */}
      {activeTab === 'activity' && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Aktivitätslog</h2>
          {activityLoading && (
            <div>
              <Skeleton className={styles.skeletonRow} />
              <Skeleton className={styles.skeletonRow} />
            </div>
          )}
          {!activityLoading && (
            <div className={styles.activityList}>
              {(activityData?.activity ?? []).length === 0 && (
                <div className={styles.emptyActivity}>Noch keine Aktivitäten.</div>
              )}
              {(activityData?.activity ?? []).map((entry) => (
                <div key={entry.id} className={styles.activityRow}>
                  <div className={styles.activityMeta}>
                    <span>{formatDateTime(entry.created_at)}</span>
                    <span className={styles.activityActor}>
                      {entry.actor_type === 'admin'
                        ? entry.user_name ?? entry.user_email ?? 'Admin'
                        : entry.actor_type}
                    </span>
                  </div>
                  <div className={styles.activityAction}>
                    {ACTION_LABELS[entry.action] ?? entry.action}
                  </div>
                  {entry.details && (
                    <div className={styles.activityDetails}>
                      {JSON.stringify(entry.details, null, 2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Extracted data modal */}
      {viewingExtracted && (
        <div className={styles.overlay} onClick={() => setViewingExtracted(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <span className={styles.modalTitle}>
                Extrahierte Daten — {viewingExtracted.filename}
              </span>
              <button className={styles.modalClose} onClick={() => setViewingExtracted(null)}>
                ×
              </button>
            </div>
            <pre className={styles.jsonView}>
              {JSON.stringify(viewingExtracted.data, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={publishConfirmOpen}
        title="Ergebnisse veröffentlichen?"
        body="Die Analyseergebnisse werden für den Kunden sichtbar. Der Projektstatus wird auf 'Ergebnis verfügbar' gesetzt und der Kunde wird benachrichtigt."
        confirmLabel="Veröffentlichen"
        cancelLabel="Abbrechen"
        onConfirm={handlePublish}
        onCancel={() => setPublishConfirmOpen(false)}
        loading={publishing}
      />
    </div>
  )
}
